import * as React from 'react';
import { Select } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import type { EntityDataSourceType } from '@web-config-app/core';
import {
  useDataSource,
  useEntityFormDataSource,
} from '@web-config-app/entity-form';
import type { ValueCatalogMapping } from './types';

/**
 * A `<select>` whose options come from a registry data source. Used by the
 * branching/`goTo` config for the destination picker, and by the visibility
 * config (`dataSource` subject) for the subject-question picker — both are
 * "pick a linkId from a registered list", so the picker is shared.
 */
interface DestinationOption {
  value: string;
  label: string;
}

/**
 * `useEntityFormDataSource` returns a value that varies by consumer type; for
 * an `enumOptions` consumer it's `{ enumOptions: [...] }`. Narrow to that shape
 * with a type guard (no cast); any other shape is treated as "no options".
 */
const hasEnumOptions = (
  value: unknown,
): value is { enumOptions: DestinationOption[] } =>
  typeof value === 'object' &&
  value !== null &&
  'enumOptions' in value &&
  Array.isArray(value.enumOptions);

const useDestinationOptions = (
  dataSourceName: string | undefined,
  dataSourceType: EntityDataSourceType,
  mapping: ValueCatalogMapping | undefined,
): DestinationOption[] => {
  const dataSource = useDataSource(dataSourceName ?? '');
  const value = useEntityFormDataSource({
    // When the schema doesn't declare a data source for the action
    // field, render no options. A minimal `EntityDataSource` stub keeps
    // `useEntityFormDataSource` happy without throwing on
    // `dataSource.config` / `dataSource.value` reads. (The missing
    // declaration is warned about once, in `ConditionalActionControl`.)
    dataSource: dataSource ?? {
      name: dataSourceName ?? '',
      type: 'entity',
      config: {},
      value: [],
    },
    dataSourceConsumer: {
      type: dataSourceType,
      dataSource: dataSourceName ?? '',
      mapping,
    },
  });
  return hasEnumOptions(value) ? value.enumOptions : [];
};

interface GoToPickerProps {
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  placeholderKey: string;
  enabled: boolean;
  /**
   * Name of the entity data source supplying options. Read from the
   * action/source field's `x-entity-data-source.dataSource` in the schema.
   * The picker no longer hardcodes `questionnaireQuestions` — different
   * consumers can plug in any registered data source.
   */
  dataSourceName: string | undefined;
  /**
   * Consumer type declared on the field's `x-entity-data-source.type`.
   * Honored rather than hardcoded so the schema declaration and the runtime
   * behavior can't diverge.
   */
  dataSourceType: EntityDataSourceType;
  /**
   * Mapping for raw data-source items into `{value, label}` enum
   * options. Read from the same `x-entity-data-source.mapping`.
   */
  mapping?: ValueCatalogMapping;
  /**
   * Field identity + association props, forwarded to the underlying
   * `<select>`. Genesis `FormField` works by cloneElement-ing its DIRECT
   * child with `id`/`name`/`aria-describedby` — when this picker is that
   * child (the default-destination usage), these arrive injected and MUST
   * reach the Select or the rendered `<label for>` points at nothing.
   * The per-rule THEN usage passes `id` explicitly and is labelled by
   * the visible "go to" pill (`<Pill htmlFor>`), so no aria-label is
   * needed in either usage — both have real `<label>` elements.
   */
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  /**
   * Backward-only scope: when set to a value present in the source, the
   * picker offers only options that appear BEFORE it in source order
   * (excluding it). Used by the visibility subject picker so a question can
   * only reference EARLIER questions — its own and later linkIds drop out,
   * and a stored forward/self reference surfaces as the "missing" entry
   * below, prompting the author to fix it. Omit for unrestricted pickers
   * (e.g. the goTo destination, which may target any question).
   */
  limitToBefore?: string;
}

export const GoToPicker: React.FC<GoToPickerProps> = ({
  value,
  onChange,
  placeholderKey,
  enabled,
  dataSourceName,
  dataSourceType,
  mapping,
  id,
  name,
  'aria-describedby': ariaDescribedby,
  limitToBefore,
}) => {
  const { formatMessage } = useIntl();
  const allOptions = useDestinationOptions(
    dataSourceName,
    dataSourceType,
    mapping,
  );

  // Backward-only scope. When `limitToBefore` resolves in the list, keep
  // only the options preceding it (excludes itself and everything after);
  // when it isn't found, don't over-filter — fall back to the full list.
  const beforeIndex = limitToBefore
    ? allOptions.findIndex((option) => option.value === limitToBefore)
    : -1;
  const options =
    beforeIndex >= 0 ? allOptions.slice(0, beforeIndex) : allOptions;

  // A stored destination that no longer resolves to any option (e.g.,
  // the target question was deleted) must stay VISIBLE — otherwise the
  // native select falls back to showing the placeholder while the data
  // still holds the dangling reference, and display silently diverges
  // from storage. Surface it as an explicit "missing" entry the author
  // can see and change.
  const valueIsKnown =
    !value || options.some((option) => option.value === value);
  const optionList = valueIsKnown
    ? options
    : [
        {
          value,
          label: formatMessage(
            { id: 'CONDITIONAL_ACTION_MISSING_DESTINATION' },
            { id: value },
          ),
        },
        ...options,
      ];

  return (
    <Select
      id={id}
      name={name ?? id}
      aria-describedby={ariaDescribedby}
      value={value ?? ''}
      onChange={(evt) => onChange(evt.currentTarget.value || undefined)}
      disabled={!enabled}
      options={[
        { value: '', label: formatMessage({ id: placeholderKey }) },
        ...optionList,
      ]}
    />
  );
};
